/**
 * Catalogue conventions and the two reference playthroughs, shared by the unit tests, the API's
 * publish gate and the editor's checks panel so that "what makes a scenario good enough to run" is
 * decided in exactly one place.
 *
 * Conventions are warnings, not schema errors: a draft may break them while it is being written,
 * and a facilitator's own scenario may publish with some of them open; the catalogue tests turn them
 * into failures because a template is held to a higher standard.
 */
import {
  advance,
  createRun,
  currentNode,
  dimensionScores,
  type RunState,
  readinessScore,
  resolveDecision,
} from "./engine";
import type { DecisionOption, Scenario } from "./schema";
import type { ValidationIssue } from "./validate";

const QUALITY_ORDER = ["bad", "risky", "good", "best"] as const;

/** Walk a scenario to its debrief, choosing an option with `pick` at every decision. */
export function playScenario(
  scenario: Scenario,
  pick: (options: DecisionOption[]) => DecisionOption,
): { run: RunState; chosen: DecisionOption[] } {
  let run = createRun(scenario);
  const chosen: DecisionOption[] = [];
  // A draft may point at a node that does not exist yet; the engine throws, and here that simply
  // means the walk did not finish (the graph validator names the missing node).
  try {
    for (let guard = 0; guard < 128 && !run.finished; guard++) {
      const node = currentNode(scenario, run);
      if (node.type === "decision") {
        const option = pick(node.options);
        chosen.push(option);
        run = resolveDecision(scenario, run, {
          tally: { [option.id]: 5 },
          totalVotes: 5,
          eligibleVoters: 5,
          openMs: 30_000,
          now: 0,
        }).state;
        continue;
      }
      const next = advance(scenario, run);
      if (next === run) break;
      run = next;
    }
  } catch {
    run = { ...run, finished: false };
  }
  return { run, chosen };
}

export const bestOf = (options: DecisionOption[]): DecisionOption =>
  [...options].sort(
    (a, b) => QUALITY_ORDER.indexOf(b.quality) - QUALITY_ORDER.indexOf(a.quality),
  )[0] as DecisionOption;

export const worstOf = (options: DecisionOption[]): DecisionOption =>
  [...options].sort(
    (a, b) => QUALITY_ORDER.indexOf(a.quality) - QUALITY_ORDER.indexOf(b.quality),
  )[0] as DecisionOption;

export interface PlaythroughSummary {
  best: { finished: boolean; decisions: number; readiness: number | null; unexercised: string[] };
  worst: { finished: boolean; decisions: number; readiness: number | null };
}

/** The two reference rooms: one that always picks the best option, one that always picks the worst. */
export function playthroughSummary(scenario: Scenario): PlaythroughSummary {
  const best = playScenario(scenario, bestOf);
  const worst = playScenario(scenario, worstOf);
  const unexercised = Object.entries(dimensionScores(scenario, best.run))
    .filter(([, v]) => v === null)
    .map(([dim]) => dim);
  return {
    best: {
      finished: best.run.finished,
      decisions: best.chosen.length,
      readiness: readinessScore(scenario, best.run),
      unexercised,
    },
    worst: {
      finished: worst.run.finished,
      decisions: worst.chosen.length,
      readiness: readinessScore(scenario, worst.run),
    },
  };
}

/** Thresholds the catalogue tests enforce; the editor shows them as targets. */
export const PLAYTHROUGH_THRESHOLDS = { bestReadinessMin: 80, worstReadinessMax: 40, minDecisions: 3 };

/**
 * Authoring conventions as warnings with the path of the thing to fix. Every one of them is a
 * lesson from running the catalogue: a decision nobody can get wrong teaches nothing, a call
 * without tells has nothing to reveal, a scenario without sources is a story rather than training.
 */
export function checkConventions(s: Scenario): ValidationIssue[] {
  const out: ValidationIssue[] = [];
  const warn = (path: string, message: string) => out.push({ level: "warning", path, message });

  let decisions = 0;
  for (const [nodeId, node] of Object.entries(s.nodes)) {
    if (node.type === "decision") {
      decisions++;
      const qualities = new Set(node.options.map((o) => o.quality));
      if (!qualities.has("best")) warn(`nodes.${nodeId}.options`, "no option is marked best");
      if (!qualities.has("bad") && !qualities.has("risky")) {
        warn(`nodes.${nodeId}.options`, "nothing for a rushed room to get wrong: add a risky or bad option");
      }
      if (!node.options.some((o) => o.roleBonus.length > 0)) {
        warn(`nodes.${nodeId}.options`, "no option names a role whose job it is (roleBonus)");
      }
      for (const o of node.options) {
        if (o.feedback.length <= 24) {
          warn(`nodes.${nodeId}.options.${o.id}.feedback`, "feedback is too thin to teach from");
        }
      }
    }
    if (node.type === "call") {
      if (!node.redFlags.length) warn(`nodes.${nodeId}.redFlags`, "the call lists no red flags");
      if (!node.transcript.some((line) => Boolean(line.tell))) {
        warn(`nodes.${nodeId}.transcript`, "no line of the call carries a tell");
      }
    }
  }
  if (!s.basedOn.length) warn("basedOn", "cite the incident or report the scenario is modelled on");
  if (s.learningObjectives.length < 3) warn("learningObjectives", "fewer than three learning objectives");
  if (!s.injects.length) warn("injects", "no injects: the facilitator has no curveball to throw");
  if (!s.badges.length) warn("badges", "no badges");
  if (decisions < PLAYTHROUGH_THRESHOLDS.minDecisions) {
    warn("nodes", `fewer than ${PLAYTHROUGH_THRESHOLDS.minDecisions} decisions`);
  }

  const play = playthroughSummary(s);
  if (!play.best.finished) warn("start", "a room that always picks the best option never reaches a debrief");
  else if ((play.best.readiness ?? 0) < PLAYTHROUGH_THRESHOLDS.bestReadinessMin) {
    warn(
      "nodes",
      `a room that always picks the best option scores ${play.best.readiness ?? 0}, under ${PLAYTHROUGH_THRESHOLDS.bestReadinessMin}`,
    );
  }
  for (const dim of play.best.unexercised) {
    warn(`dimensions.${dim}`, "no decision on the best path scores this dimension");
  }
  if (!play.worst.finished)
    warn("start", "a room that always picks the worst option never reaches a debrief");
  else if ((play.worst.readiness ?? 100) > PLAYTHROUGH_THRESHOLDS.worstReadinessMax) {
    warn(
      "nodes",
      `a room that always picks the worst option still scores ${play.worst.readiness}, over ${PLAYTHROUGH_THRESHOLDS.worstReadinessMax}`,
    );
  }
  return out;
}
